/* static/table_minibar.js
   Generic floating/docked minibar controller.

   Public API:
   - window.MbbTableMinibar.create(options) -> controller
   - window.MbbTableMinibar.init(options)   -> initialized controller

   Default behavior preserves legacy app wiring:
   - auto boot on DOMContentLoaded
   - bar selector:  .floating-mini-toolbar[data-actionbar='1']
   - dock selector: [data-actionbar-dock='1']
*/

(function bootstrapTableMinibar(global) {
  "use strict";

  const VERSION = "2.0.0";

  const DEFAULT_OPTIONS = Object.freeze({
    root: null,
    bar: null,
    dock: null,
    selectors: {
      bar: ".floating-mini-toolbar[data-actionbar='1']",
      handle: ".ab-handle",
      dock: "[data-actionbar-dock='1']",
      button: ".fmt-btn",
      tooltip: ".ab-tooltip",
      topbar: ".top-bar, header.top-bar, header",
      contentWrapper: ".content-wrapper",
      mainContent: ".main-content",
      stickyOverlay: ".ab-sticky-overlay",
      panel: ".table-container",
      overlayHeadings: "h2, h1",
    },
    storage: {
      positionKey: "mbb.actionbar.pos.v1",
      collapsedKey: "mbb.actionbar.collapsed.v1",
      persistPosition: true,
      persistCollapsed: true,
    },
    layout: {
      defaultRightPad: 18,
      defaultTopPad: 10,
      gapBelowTopbar: 12,
      stickyTranslateMax: 600,
      stabilizeTimeoutMs: 650,
    },
    behavior: {
      autoDock: true,
      addToggle: true,
      enableDrag: true,
      enableTooltip: true,
      enableBusyHook: true,
      enablePulseHook: true,
      enableStickyOverlay: true,
      enableIdleFade: true,
      enableSnap: true,
      snapPx: 18,
      keyStep: 10,
      keyStepFast: 40,
      idleMs: 1600,
      defaultCollapsedFloating: true,
      defaultCollapsedDocked: false,
    },
    hooks: {
      spinnerShowName: "showSpinner",
      spinnerHideName: "hideSpinner",
      notifyObjectName: "notify",
    },
  });

  function mergeOptions(userOptions = {}) {
    const user = userOptions && typeof userOptions === "object" ? userOptions : {};

    return {
      ...DEFAULT_OPTIONS,
      ...user,
      selectors: {
        ...DEFAULT_OPTIONS.selectors,
        ...(user.selectors && typeof user.selectors === "object" ? user.selectors : {}),
      },
      storage: {
        ...DEFAULT_OPTIONS.storage,
        ...(user.storage && typeof user.storage === "object" ? user.storage : {}),
      },
      layout: {
        ...DEFAULT_OPTIONS.layout,
        ...(user.layout && typeof user.layout === "object" ? user.layout : {}),
      },
      behavior: {
        ...DEFAULT_OPTIONS.behavior,
        ...(user.behavior && typeof user.behavior === "object" ? user.behavior : {}),
      },
      hooks: {
        ...DEFAULT_OPTIONS.hooks,
        ...(user.hooks && typeof user.hooks === "object" ? user.hooks : {}),
      },
    };
  }

  function isElement(x) {
    return !!(x && typeof x === "object" && x.nodeType === 1);
  }

  function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n));
  }

  function createTableMinibar(userOptions = {}) {
    const options = mergeOptions(userOptions);

    const doc = options.document || global.document;
    const win = options.window || global;
    const root =
      isElement(options.root) || options.root === doc
        ? options.root
        : (isElement(doc?.body) ? doc : null);

    const state = {
      initialized: false,
      destroyed: false,
      userMoved: false,
      defaultFinalized: false,
      idleTimer: null,
      bar: null,
      dock: null,
      isDocked: false,
      cleanupFns: [],
      hookOriginals: {
        showSpinner: null,
        hideSpinner: null,
        notifyOk: null,
        notifyErr: null,
        notifyObj: null,
      },
      hookWrappers: {
        showSpinner: null,
        hideSpinner: null,
        notifyOk: null,
        notifyErr: null,
      },
      createdTooltip: null,
      controller: null,
    };

    function listen(target, eventName, handler, eventOpts) {
      if (!target || typeof target.addEventListener !== "function") return;
      target.addEventListener(eventName, handler, eventOpts);
      state.cleanupFns.push(() => {
        try {
          target.removeEventListener(eventName, handler, eventOpts);
        } catch {}
      });
    }

    function cleanupListeners() {
      while (state.cleanupFns.length) {
        const fn = state.cleanupFns.pop();
        try {
          fn?.();
        } catch {}
      }
    }

    function clearIdleTimer() {
      if (!state.idleTimer) return;
      clearTimeout(state.idleTimer);
      state.idleTimer = null;
    }

    function queryWithin(scope, selector) {
      if (!scope || !selector || typeof selector !== "string") return null;
      try {
        return scope.querySelector(selector);
      } catch {
        return null;
      }
    }

    function query(selector) {
      if (!selector || typeof selector !== "string") return null;
      if (isElement(root)) {
        const el = queryWithin(root, selector);
        if (el) return el;
      }
      return queryWithin(doc, selector);
    }

    function resolveElement(ref, fallbackSelector) {
      if (isElement(ref)) return ref;
      if (typeof ref === "string" && ref.trim()) return query(ref.trim());
      return query(fallbackSelector);
    }

    function getBar() {
      if (state.bar && state.bar.isConnected) return state.bar;
      state.bar = resolveElement(options.bar, options.selectors.bar);
      return state.bar;
    }

    function getDockTarget() {
      if (state.dock && state.dock.isConnected) return state.dock;
      state.dock = resolveElement(options.dock, options.selectors.dock);
      return state.dock;
    }

    function getTopbarBottom() {
      const topbar = query(options.selectors.topbar);
      if (!topbar) return 0;
      const rect = topbar.getBoundingClientRect();
      return Number.isFinite(rect.bottom) ? rect.bottom : 0;
    }

    function ensureLayout(el) {
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      return el.getBoundingClientRect();
    }

    function rightFromLeft(barWidth, left) {
      return win.innerWidth - (left + barWidth);
    }

    function leftFromRight(barWidth, right) {
      return win.innerWidth - barWidth - right;
    }

    function clampRightTop(bar, right, top) {
      const rect = ensureLayout(bar);
      const maxRight = Math.max(0, win.innerWidth - rect.width);
      const maxTop = Math.max(0, win.innerHeight - rect.height);
      return {
        right: clamp(right, 0, maxRight),
        top: clamp(top, 0, maxTop),
      };
    }

    function maybeSnapRightTop(bar, right, top) {
      if (!options.behavior.enableSnap) return { right, top };

      const rect = ensureLayout(bar);
      const maxLeft = Math.max(0, win.innerWidth - rect.width);
      const maxTop = Math.max(0, win.innerHeight - rect.height);
      const snap = Number(options.behavior.snapPx) || 0;

      let left = leftFromRight(rect.width, right);
      let y = top;

      if (left <= snap) left = 0;
      if (Math.abs(maxLeft - left) <= snap) left = maxLeft;

      if (y <= snap) y = 0;
      if (Math.abs(maxTop - y) <= snap) y = maxTop;

      return { right: rightFromLeft(rect.width, left), top: y };
    }

    function applyPosRightTop(bar, right, top) {
      bar.style.position = "fixed";
      bar.style.right = `${right}px`;
      bar.style.top = `${top}px`;
      bar.style.left = "auto";
      bar.style.bottom = "auto";
    }

    function clearFixedPos(bar) {
      bar.style.position = "";
      bar.style.left = "";
      bar.style.top = "";
      bar.style.right = "";
      bar.style.bottom = "";
    }

    function hideBar(bar) {
      bar.style.opacity = "0";
      bar.style.pointerEvents = "none";
    }

    function showBar(bar) {
      bar.style.opacity = "";
      bar.style.pointerEvents = "";
    }

    function savePos(right, top) {
      if (!options.storage.persistPosition || !options.storage.positionKey) return;
      try {
        win.localStorage.setItem(options.storage.positionKey, JSON.stringify({ right, top }));
      } catch {}
    }

    function loadPos(bar) {
      if (!options.storage.persistPosition || !options.storage.positionKey) return null;

      try {
        const raw = win.localStorage.getItem(options.storage.positionKey);
        if (!raw) return null;

        const p = JSON.parse(raw);
        if (!p || !Number.isFinite(p.top)) return null;

        if (Number.isFinite(p.right)) {
          return { right: p.right, top: p.top };
        }

        if (Number.isFinite(p.left)) {
          const rect = ensureLayout(bar);
          const migrated = {
            right: rightFromLeft(rect.width, p.left),
            top: p.top,
          };
          const c = clampRightTop(bar, migrated.right, migrated.top);
          const snapped = maybeSnapRightTop(bar, c.right, c.top);
          savePos(snapped.right, snapped.top);
          return { right: snapped.right, top: snapped.top };
        }
      } catch {}

      return null;
    }

    function clearSavedPos() {
      if (!options.storage.positionKey) return;
      try {
        win.localStorage.removeItem(options.storage.positionKey);
      } catch {}
    }

    function saveCollapsed(v) {
      if (!options.storage.persistCollapsed || !options.storage.collapsedKey) return;
      try {
        win.localStorage.setItem(options.storage.collapsedKey, JSON.stringify(!!v));
      } catch {}
    }

    function loadCollapsed() {
      if (!options.storage.persistCollapsed || !options.storage.collapsedKey) return null;
      try {
        const raw = win.localStorage.getItem(options.storage.collapsedKey);
        if (raw == null) return null;
        return !!JSON.parse(raw);
      } catch {
        return null;
      }
    }

    function setCollapsed(bar, collapsed) {
      bar.classList.toggle("ab-collapsed", !!collapsed);
      saveCollapsed(!!collapsed);
    }

    function toggleCollapsed() {
      const bar = getBar();
      if (!bar) return false;
      const next = !bar.classList.contains("ab-collapsed");
      setCollapsed(bar, next);
      return next;
    }

    function computeDefaultLeftTop(bar) {
      const desiredTop = Math.max(
        Number(options.layout.defaultTopPad) || 0,
        Math.round(getTopbarBottom() + (Number(options.layout.gapBelowTopbar) || 0))
      );

      const rect = ensureLayout(bar);

      const canvas =
        query(options.selectors.contentWrapper) ||
        query(options.selectors.mainContent);

      let desiredLeft;
      let desiredTop2 = desiredTop;

      if (canvas) {
        const cr = canvas.getBoundingClientRect();
        desiredLeft = Math.round(cr.right - rect.width - (Number(options.layout.defaultRightPad) || 0));
        desiredTop2 = Math.max(desiredTop2, Math.round(cr.top + (Number(options.layout.defaultTopPad) || 0)));
      } else {
        desiredLeft = Math.round(win.innerWidth - rect.width - (Number(options.layout.defaultRightPad) || 0));
      }

      const maxLeft = Math.max(0, win.innerWidth - rect.width);
      const maxTop = Math.max(0, win.innerHeight - rect.height);

      return {
        left: clamp(desiredLeft, 0, maxLeft),
        top: clamp(desiredTop2, 0, maxTop),
      };
    }

    function finalizeDefaultPos(bar) {
      const rect = ensureLayout(bar);
      const d = computeDefaultLeftTop(bar);
      const right = rightFromLeft(rect.width, d.left);

      const c = clampRightTop(bar, right, d.top);
      const snapped = maybeSnapRightTop(bar, c.right, c.top);

      applyPosRightTop(bar, snapped.right, snapped.top);
      savePos(snapped.right, snapped.top);
    }

    function resetToDefault(bar) {
      clearSavedPos();
      finalizeDefaultPos(bar);
    }

    function finalizeDefaultOnceWhenStable(bar) {
      if (state.defaultFinalized) return;
      if (state.userMoved) return;
      if (loadPos(bar)) return;

      state.defaultFinalized = true;

      const loadPromise = new Promise((resolve) => {
        if (doc.readyState === "complete") {
          resolve();
          return;
        }
        listen(win, "load", resolve, { once: true });
      });

      const fontsPromise =
        doc.fonts && doc.fonts.ready && typeof doc.fonts.ready.then === "function"
          ? doc.fonts.ready.catch(() => {})
          : Promise.resolve();

      const timeoutPromise = new Promise((resolve) => {
        setTimeout(resolve, Number(options.layout.stabilizeTimeoutMs) || 650);
      });

      Promise.race([Promise.allSettled([loadPromise, fontsPromise]), timeoutPromise]).then(() => {
        if (state.userMoved) {
          showBar(bar);
          return;
        }
        if (loadPos(bar)) {
          showBar(bar);
          return;
        }

        finalizeDefaultPos(bar);
        showBar(bar);
      });
    }

    function setIdle(bar, isIdle) {
      bar.classList.toggle("ab-idle", !!isIdle);
    }

    function armIdle(bar) {
      clearIdleTimer();
      state.idleTimer = setTimeout(() => setIdle(bar, true), Number(options.behavior.idleMs) || 1600);
    }

    function wake(bar) {
      clearIdleTimer();
      setIdle(bar, false);
      armIdle(bar);
    }

    function ensureToggleBtn(bar) {
      if (!options.behavior.addToggle) return null;

      let btn = bar.querySelector(".ab-toggle");
      if (btn) return btn;

      btn = doc.createElement("button");
      btn.type = "button";
      btn.className = "fmt-btn ab-toggle";
      btn.setAttribute("aria-label", "Toggle toolbar");
      btn.dataset.tip = "Toggle actions";

      btn.innerHTML = [
        '<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">',
        '<circle cx="6" cy="12" r="1.7"></circle>',
        '<circle cx="12" cy="12" r="1.7"></circle>',
        '<circle cx="18" cy="12" r="1.7"></circle>',
        "</svg>",
      ].join("");

      const handle = bar.querySelector(options.selectors.handle);
      if (handle && handle.nextSibling) bar.insertBefore(btn, handle.nextSibling);
      else bar.appendChild(btn);

      return btn;
    }

    function initTooltip(bar) {
      if (!options.behavior.enableTooltip) return;

      const btnSelector = options.selectors.button || ".fmt-btn";
      bar.querySelectorAll(`${btnSelector}[title]`).forEach((btn) => btn.removeAttribute("title"));

      let tip = queryWithin(doc, options.selectors.tooltip || ".ab-tooltip");
      if (!tip) {
        tip = doc.createElement("div");
        tip.className = (options.selectors.tooltip || ".ab-tooltip").replace(/^\./, "");
        tip.setAttribute("role", "tooltip");
        tip.setAttribute("aria-hidden", "true");
        doc.body.appendChild(tip);
        state.createdTooltip = tip;
      }

      let visible = false;
      let currentBtn = null;

      function setTip(text) {
        tip.textContent = text || "";
      }

      function placeTip(x, y) {
        const pad = 10;
        const rect = tip.getBoundingClientRect();
        const xx = clamp(x, pad + rect.width / 2, win.innerWidth - pad - rect.width / 2);
        const yy = clamp(y, pad + rect.height, win.innerHeight - pad);
        tip.style.left = `${xx}px`;
        tip.style.top = `${yy}px`;
      }

      function show(text, x, y) {
        if (!text) return;
        setTip(text);
        placeTip(x, y);
        tip.classList.add("show");
        tip.setAttribute("aria-hidden", "false");
        visible = true;
      }

      function hide() {
        if (!visible) return;
        tip.classList.remove("show");
        tip.setAttribute("aria-hidden", "true");
        visible = false;
        currentBtn = null;
      }

      function textForBtn(btn) {
        return btn?.dataset?.tip || btn?.getAttribute("aria-label") || "";
      }

      listen(bar, "pointerover", (e) => {
        const btn = e.target.closest(btnSelector);
        if (!btn) return;

        if (currentBtn !== btn) {
          currentBtn = btn;
          const text = textForBtn(btn);
          if (!text) return;
          show(text, e.clientX, e.clientY - 10);
        }
      });

      listen(bar, "pointermove", (e) => {
        if (!visible) return;
        placeTip(e.clientX, e.clientY - 10);
      });

      listen(bar, "pointerout", (e) => {
        const fromBtn = e.target.closest(btnSelector);
        if (!fromBtn) return;

        const toBtn = e.relatedTarget?.closest ? e.relatedTarget.closest(btnSelector) : null;
        if (fromBtn && !toBtn) hide();
      });

      listen(bar, "focusin", (e) => {
        const btn = e.target.closest(btnSelector);
        if (!btn) return;

        currentBtn = btn;
        const text = textForBtn(btn);
        if (!text) return;

        const r = btn.getBoundingClientRect();
        show(text, r.left + r.width / 2, r.top);
      });

      listen(bar, "focusout", hide);
      listen(win, "scroll", hide, { passive: true });
      listen(doc, "keydown", (e) => {
        if (e.key === "Escape") hide();
      });
    }

    function initDrag(bar) {
      if (!options.behavior.enableDrag) return;

      const handle = bar.querySelector(options.selectors.handle);
      if (!handle) return;

      let dragging = false;
      let startX = 0;
      let startY = 0;
      let startRight = 0;
      let startTop = 0;
      let moved = 0;

      listen(handle, "pointerdown", (e) => {
        if (e.detail && e.detail > 1) return;

        const rect = ensureLayout(bar);

        dragging = true;
        moved = 0;
        bar.classList.add("ab-dragging");

        startX = e.clientX;
        startY = e.clientY;

        startRight = Math.max(0, win.innerWidth - rect.right);
        startTop = rect.top;

        handle.setPointerCapture?.(e.pointerId);
        wake(bar);
        e.preventDefault();
        e.stopPropagation();
      });

      listen(handle, "pointermove", (e) => {
        if (!dragging) return;

        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        moved = Math.max(moved, Math.abs(dx) + Math.abs(dy));

        const nextRight = startRight - dx;
        const nextTop = startTop + dy;

        const c = clampRightTop(bar, nextRight, nextTop);
        applyPosRightTop(bar, c.right, c.top);

        e.preventDefault();
        e.stopPropagation();
      });

      listen(handle, "pointerup", (e) => {
        if (!dragging) return;

        dragging = false;
        bar.classList.remove("ab-dragging");

        if (moved > 6) {
          state.userMoved = true;

          const rect = ensureLayout(bar);
          const curRight = Math.max(0, win.innerWidth - rect.right);
          const curTop = rect.top;

          const c = clampRightTop(bar, curRight, curTop);
          const snapped = maybeSnapRightTop(bar, c.right, c.top);

          applyPosRightTop(bar, snapped.right, snapped.top);
          savePos(snapped.right, snapped.top);
        }

        try {
          handle.releasePointerCapture?.(e.pointerId);
        } catch {}

        armIdle(bar);
        e.preventDefault();
        e.stopPropagation();
      });

      listen(handle, "dblclick", (e) => {
        state.userMoved = true;
        resetToDefault(bar);
        wake(bar);
        e.preventDefault();
        e.stopPropagation();
      });

      listen(handle, "keydown", (e) => {
        const step = e.shiftKey
          ? Number(options.behavior.keyStepFast) || 40
          : Number(options.behavior.keyStep) || 10;

        const rect = ensureLayout(bar);
        let right = Math.max(0, win.innerWidth - rect.right);
        let top = rect.top;

        let used = true;

        switch (e.key) {
          case "ArrowLeft":
            right += step;
            break;
          case "ArrowRight":
            right -= step;
            break;
          case "ArrowUp":
            top -= step;
            break;
          case "ArrowDown":
            top += step;
            break;
          case "Enter":
          case " ":
          case "Escape":
            state.userMoved = true;
            resetToDefault(bar);
            wake(bar);
            e.preventDefault();
            e.stopPropagation();
            return;
          default:
            used = false;
        }

        if (!used) return;

        state.userMoved = true;
        const c = clampRightTop(bar, right, top);
        const snapped = maybeSnapRightTop(bar, c.right, c.top);

        applyPosRightTop(bar, snapped.right, snapped.top);
        savePos(snapped.right, snapped.top);
        wake(bar);

        e.preventDefault();
        e.stopPropagation();
      });

      listen(win, "resize", () => {
        const rect = ensureLayout(bar);
        const right = Math.max(0, win.innerWidth - rect.right);
        const top = rect.top;

        const c = clampRightTop(bar, right, top);
        const snapped = maybeSnapRightTop(bar, c.right, c.top);

        applyPosRightTop(bar, snapped.right, snapped.top);
        savePos(snapped.right, snapped.top);
      });
    }

    function patchBusyAndPulse(bar) {
      const spinnerShowName = String(options.hooks.spinnerShowName || "showSpinner");
      const spinnerHideName = String(options.hooks.spinnerHideName || "hideSpinner");
      const notifyObjectName = String(options.hooks.notifyObjectName || "notify");

      function pulse(cssClass) {
        bar.classList.remove("ab-pulse-ok", "ab-pulse-err");
        bar.classList.add(cssClass);
        setTimeout(() => bar.classList.remove(cssClass), 420);
      }

      if (options.behavior.enableBusyHook) {
        const origShow = global[spinnerShowName];
        const origHide = global[spinnerHideName];

        if (typeof origShow === "function") {
          state.hookOriginals.showSpinner = origShow;
          state.hookWrappers.showSpinner = function patchedShowSpinner(...args) {
            bar.classList.add("ab-busy");
            return origShow.apply(this, args);
          };
          global[spinnerShowName] = state.hookWrappers.showSpinner;
        }

        if (typeof origHide === "function") {
          state.hookOriginals.hideSpinner = origHide;
          state.hookWrappers.hideSpinner = function patchedHideSpinner(...args) {
            bar.classList.remove("ab-busy");
            return origHide.apply(this, args);
          };
          global[spinnerHideName] = state.hookWrappers.hideSpinner;
        }
      }

      if (options.behavior.enablePulseHook) {
        const notifyObj = global[notifyObjectName];
        if (notifyObj && typeof notifyObj === "object") {
          state.hookOriginals.notifyObj = notifyObj;

          if (typeof notifyObj.ok === "function") {
            state.hookOriginals.notifyOk = notifyObj.ok;
            state.hookWrappers.notifyOk = function patchedNotifyOk(...args) {
              pulse("ab-pulse-ok");
              return state.hookOriginals.notifyOk.apply(this, args);
            };
            notifyObj.ok = state.hookWrappers.notifyOk;
          }

          if (typeof notifyObj.err === "function") {
            state.hookOriginals.notifyErr = notifyObj.err;
            state.hookWrappers.notifyErr = function patchedNotifyErr(...args) {
              pulse("ab-pulse-err");
              return state.hookOriginals.notifyErr.apply(this, args);
            };
            notifyObj.err = state.hookWrappers.notifyErr;
          }
        }
      }
    }

    function restoreBusyAndPulseHooks() {
      const spinnerShowName = String(options.hooks.spinnerShowName || "showSpinner");
      const spinnerHideName = String(options.hooks.spinnerHideName || "hideSpinner");

      if (
        state.hookWrappers.showSpinner &&
        state.hookOriginals.showSpinner &&
        global[spinnerShowName] === state.hookWrappers.showSpinner
      ) {
        global[spinnerShowName] = state.hookOriginals.showSpinner;
      }

      if (
        state.hookWrappers.hideSpinner &&
        state.hookOriginals.hideSpinner &&
        global[spinnerHideName] === state.hookWrappers.hideSpinner
      ) {
        global[spinnerHideName] = state.hookOriginals.hideSpinner;
      }

      const notifyObj = state.hookOriginals.notifyObj;
      if (notifyObj && typeof notifyObj === "object") {
        if (
          state.hookWrappers.notifyOk &&
          state.hookOriginals.notifyOk &&
          notifyObj.ok === state.hookWrappers.notifyOk
        ) {
          notifyObj.ok = state.hookOriginals.notifyOk;
        }

        if (
          state.hookWrappers.notifyErr &&
          state.hookOriginals.notifyErr &&
          notifyObj.err === state.hookWrappers.notifyErr
        ) {
          notifyObj.err = state.hookOriginals.notifyErr;
        }
      }

      state.hookOriginals = {
        showSpinner: null,
        hideSpinner: null,
        notifyOk: null,
        notifyErr: null,
        notifyObj: null,
      };

      state.hookWrappers = {
        showSpinner: null,
        hideSpinner: null,
        notifyOk: null,
        notifyErr: null,
      };
    }

    function hardFixOverlayDockTarget(dockEl) {
      if (!dockEl || !options.behavior.enableStickyOverlay) return;

      const overlay = dockEl.closest(options.selectors.stickyOverlay);
      if (!overlay) return;

      dockEl.style.setProperty("position", "absolute", "important");
      dockEl.style.setProperty("top", "0px", "important");
      dockEl.style.setProperty("left", "auto", "important");
      dockEl.style.setProperty("right", "var(--ab-overlay-x, 0px)", "important");
      dockEl.style.setProperty("bottom", "auto", "important");

      overlay.style.setProperty("height", "1px", "important");
    }

    function findNearestHeaderHeadingAboveOverlay(overlay) {
      const panel =
        overlay.closest(options.selectors.panel) ||
        overlay.parentElement ||
        doc.body;

      const overlayTop = overlay.getBoundingClientRect().top;
      const heads = Array.from(panel.querySelectorAll(options.selectors.overlayHeadings));

      let best = null;
      let bestTop = -Infinity;

      heads.forEach((h) => {
        const r = h.getBoundingClientRect();
        if (r.top <= overlayTop + 8 && r.top > bestTop) {
          bestTop = r.top;
          best = h;
        }
      });

      return best;
    }

    function computeOverlayTranslateY(overlay, dockEl) {
      const overlayRect = overlay.getBoundingClientRect();
      const barEl = dockEl.querySelector(options.selectors.bar) || state.bar || dockEl;
      const barRect = barEl.getBoundingClientRect();
      const barH = barRect.height;

      const stickyTop = parseFloat(global.getComputedStyle(overlay).top) || 0;
      const minTop = Math.max(
        stickyTop,
        getTopbarBottom() + (Number(options.layout.gapBelowTopbar) || 0)
      );

      const minY = minTop - overlayRect.top;

      const cs = global.getComputedStyle(overlay);
      const raw = cs.getPropertyValue("--ab-overlay-y");
      let desiredY = Number.isFinite(parseFloat(raw)) ? parseFloat(raw) : 0;

      const heading = findNearestHeaderHeadingAboveOverlay(overlay);
      if (heading) {
        const hr = heading.getBoundingClientRect();
        desiredY = hr.bottom - barH - overlayRect.top;
      }

      const maxY = Number(options.layout.stickyTranslateMax) || 600;
      const yEff = clamp(desiredY, minY, maxY);

      overlay.style.setProperty("--ab-overlay-y-eff", `${Math.round(yEff)}px`);
    }

    function initStickyOverlayDockingIfPresent(dockEl) {
      if (!dockEl || !options.behavior.enableStickyOverlay) return;

      const overlay = dockEl.closest(options.selectors.stickyOverlay);
      if (!overlay) return;

      let ticking = false;

      const update = () => {
        ticking = false;
        try {
          computeOverlayTranslateY(overlay, dockEl);
        } catch {}
      };

      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        win.requestAnimationFrame(update);
      };

      listen(win, "scroll", requestUpdate, { passive: true });
      listen(win, "resize", requestUpdate);
      listen(win, "load", requestUpdate, { once: true });

      if (doc.fonts && doc.fonts.ready) {
        doc.fonts.ready.then(requestUpdate).catch(() => {});
      }

      requestUpdate();
    }

    function dockInto(bar, dockEl) {
      if (!dockEl || !options.behavior.autoDock) return false;

      dockEl.appendChild(bar);
      bar.classList.add("ab-docked");

      clearFixedPos(bar);
      bar.style.zIndex = "auto";
      bar.style.opacity = "";
      bar.style.pointerEvents = "";
      bar.classList.remove("ab-idle");

      return true;
    }

    function disableHandleWhenDocked(bar) {
      const handle = bar.querySelector(options.selectors.handle);
      if (!handle) return;

      handle.removeAttribute("title");
      handle.removeAttribute("role");
      handle.removeAttribute("tabindex");
      handle.setAttribute("aria-hidden", "true");
    }

    function init() {
      if (state.initialized) return state.controller;
      if (state.destroyed) return null;
      if (!doc || !win) return null;

      const bar = getBar();
      if (!bar) return null;

      state.bar = bar;

      const toggle = ensureToggleBtn(bar);
      const dockEl = getDockTarget();
      state.dock = dockEl;

      hardFixOverlayDockTarget(dockEl);

      const isDocked = dockInto(bar, dockEl);
      state.isDocked = isDocked;

      if (isDocked) disableHandleWhenDocked(bar);
      initStickyOverlayDockingIfPresent(dockEl);

      const storedCollapsed = loadCollapsed();
      if (storedCollapsed == null) {
        setCollapsed(
          bar,
          isDocked
            ? !!options.behavior.defaultCollapsedDocked
            : !!options.behavior.defaultCollapsedFloating
        );
      } else {
        setCollapsed(bar, storedCollapsed);
      }

      if (toggle) {
        listen(toggle, "click", (e) => {
          toggleCollapsed();
          e.preventDefault();
          e.stopPropagation();
        });
      }

      initTooltip(bar);
      patchBusyAndPulse(bar);

      if (isDocked) {
        showBar(bar);
        state.initialized = true;
        return state.controller;
      }

      if (options.behavior.enableIdleFade) {
        listen(bar, "pointerenter", () => wake(bar));
        listen(bar, "pointermove", () => wake(bar));
        listen(bar, "focusin", () => wake(bar));
        listen(bar, "pointerleave", () => armIdle(bar));
        listen(bar, "focusout", () => armIdle(bar));
      }

      const saved = loadPos(bar);
      if (saved) {
        const c = clampRightTop(bar, saved.right, saved.top);
        const snapped = maybeSnapRightTop(bar, c.right, c.top);
        applyPosRightTop(bar, snapped.right, snapped.top);
        savePos(snapped.right, snapped.top);
        showBar(bar);
      } else {
        hideBar(bar);
        finalizeDefaultOnceWhenStable(bar);
      }

      initDrag(bar);

      if (options.behavior.enableIdleFade) {
        setIdle(bar, true);
        armIdle(bar);
      }

      state.initialized = true;
      return state.controller;
    }

    function destroy() {
      if (state.destroyed) return;

      clearIdleTimer();
      cleanupListeners();
      restoreBusyAndPulseHooks();

      if (state.createdTooltip && state.createdTooltip.isConnected) {
        try {
          state.createdTooltip.remove();
        } catch {}
      }

      state.createdTooltip = null;
      state.initialized = false;
      state.destroyed = true;
    }

    function getState() {
      return {
        initialized: state.initialized,
        destroyed: state.destroyed,
        isDocked: state.isDocked,
        hasBar: !!state.bar,
      };
    }

    const controller = {
      version: VERSION,
      init,
      destroy,
      wake: () => {
        const bar = getBar();
        if (bar) wake(bar);
      },
      setCollapsed: (collapsed) => {
        const bar = getBar();
        if (!bar) return;
        setCollapsed(bar, collapsed);
      },
      toggleCollapsed,
      resetPosition: () => {
        const bar = getBar();
        if (!bar || state.isDocked) return;
        state.userMoved = true;
        resetToDefault(bar);
      },
      getOptions: () => options,
      getState,
      get bar() {
        return getBar();
      },
      get dock() {
        return getDockTarget();
      },
    };

    state.controller = controller;
    return controller;
  }

  function initTableMinibar(options = {}) {
    const controller = createTableMinibar(options);
    controller.init();
    return controller;
  }

  const api = Object.freeze({
    version: VERSION,
    defaults: DEFAULT_OPTIONS,
    create: createTableMinibar,
    init: initTableMinibar,
  });

  global.MbbTableMinibar = api;
  global.TableMinibar = global.TableMinibar || api;

  if (global.__MBB_TABLE_MINIBAR_AUTOBOOT_DISABLED__ !== true) {
    const autoInit = () => {
      if (global.__mbbTableMinibarDefaultInstance) return;
      const opts =
        global.__MBB_TABLE_MINIBAR_OPTIONS__ &&
        typeof global.__MBB_TABLE_MINIBAR_OPTIONS__ === "object"
          ? global.__MBB_TABLE_MINIBAR_OPTIONS__
          : {};

      global.__mbbTableMinibarDefaultInstance = api.init(opts);
    };

    const doc = global.document;
    if (doc && doc.readyState === "loading") {
      doc.addEventListener("DOMContentLoaded", autoInit, { once: true });
    } else {
      autoInit();
    }
  }
})(window);
